import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, ArrowRight, Plus } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";

export default function Packages() {
  useSeo({
    title: "Service Packages | Full Indoor Air Services",
    description: "Transparent flat-rate packages for residential and commercial air duct cleaning across MD, DC, and VA.",
    canonical: "https://capitalindoorair.com/packages",
  });

  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => {
      setPackages(r.data.packages || []);
      setAddOns(r.data.add_ons || []);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-bone" data-testid="packages-page">
      <section className="section-pad">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">Service Packages</div>
            <h1 className="font-heading text-5xl md:text-6xl tracking-tighter font-light">
              Honest, flat-rate <span className="text-forest">indoor air</span> care.
            </h1>
            <p className="mt-6 text-lg text-muted2 leading-relaxed">
              Every package includes our 7-step process, photo report, and 100% satisfaction guarantee.
              No bait-and-switch upsells.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative card-surface p-8 hover-lift flex flex-col ${
                  pkg.featured ? "ring-2 ring-clay/60" : ""
                }`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-8 bg-clay text-white text-[10px] tracking-[0.22em] uppercase px-3 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <h3 className="font-heading text-2xl tracking-tight">{pkg.name}</h3>
                <p className="text-sm text-muted2 mt-2">{pkg.tagline}</p>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-xs text-muted2">from</span>
                  <span className="font-heading text-5xl text-ink">${pkg.price_from}</span>
                </div>
                <div className="text-xs text-muted2 mt-1">{pkg.duration} · {pkg.best_for}</div>

                <ul className="mt-7 space-y-3 flex-1">
                  {pkg.highlights.map((h) => (
                    <li key={h} className="flex gap-2 text-sm text-ink/85">
                      <CheckCircle2 size={16} className="text-forest mt-0.5 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/book?package=${pkg.id}`}
                  className={`mt-8 ${pkg.featured ? "btn-primary" : "btn-ghost"} justify-center w-full`}
                  data-testid={`select-${pkg.id}`}
                >
                  Choose {pkg.name} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>

          {/* Add-ons */}
          <div className="mt-20">
            <div className="eyebrow mb-3">Add-ons</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">
              Tailor your visit.
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {addOns.map((a) => (
                <div key={a.id} className="card-surface p-6 hover-lift" data-testid={`addon-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <Plus size={18} className="text-forest" />
                    <span className="font-heading text-xl">${a.price}</span>
                  </div>
                  <div className="mt-4 font-heading text-lg tracking-tight">{a.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
